/**
 * RTWO Cryptography & Identity Engine — Algorithm Key Provider
 *
 * Shared operational key provider for elliptic-curve algorithms.
 * Truthful Data Policy: no keys generated until explicitly requested.
 */

import { ed25519 } from '@noble/curves/ed25519.js';
import { secp256k1 } from '@noble/curves/secp256k1.js';
import type {
  CryptoAlgorithm,
  EllipticCurve,
  KeyPairId,
  MessageHashHex,
  PublicKeyHex,
  RTWOKeyPair,
} from '../domain-models';
import type { KeyPairGenerationRequest } from '../service-interfaces';
import type {
  AddressProviderResult,
  IKeyProvider,
  KeyPairProviderResult,
  SignatureProviderResult,
} from '../adapters/key-provider-interface';
import { BaseKeyProvider } from './base-key-provider';
import { InMemoryKeyStore } from './crypto/key-store';
import { Sha256HashProvider } from './crypto/sha256-hash-provider';
import {
  bytesToHex,
  createKeyPairId,
  createPrivateKeyReference,
  createSignatureId,
  hexToBytes,
  isValidHex,
} from './crypto/crypto-utils';

type CurveImplementation = {
  generateKeyPair: () => { privateKey: Uint8Array; publicKey: Uint8Array };
  sign: (messageHash: Uint8Array, privateKey: Uint8Array) => Uint8Array;
  verify: (signature: Uint8Array, messageHash: Uint8Array, publicKey: Uint8Array) => boolean;
  validatePublicKey: (publicKey: Uint8Array) => boolean;
  expectedPublicKeyLength: number;
};

const CURVE_IMPLEMENTATIONS: Record<'Ed25519' | 'secp256k1', CurveImplementation> = {
  Ed25519: {
    generateKeyPair: () => {
      const { secretKey, publicKey } = ed25519.keygen();
      return { privateKey: secretKey, publicKey };
    },
    sign: (messageHash, privateKey) => ed25519.sign(messageHash, privateKey),
    verify: (signature, messageHash, publicKey) =>
      ed25519.verify(signature, messageHash, publicKey),
    validatePublicKey: (publicKey) => publicKey.length === 32,
    expectedPublicKeyLength: 64,
  },
  secp256k1: {
    generateKeyPair: () => {
      const { secretKey, publicKey } = secp256k1.keygen();
      return { privateKey: secretKey, publicKey };
    },
    sign: (messageHash, privateKey) => secp256k1.sign(messageHash, privateKey, { prehash: false }),
    verify: (signature, messageHash, publicKey) =>
      secp256k1.verify(signature, messageHash, publicKey, { prehash: false }),
    validatePublicKey: (publicKey) => publicKey.length === 33 || publicKey.length === 65,
    expectedPublicKeyLength: 66,
  },
};

export abstract class AlgorithmKeyProvider extends BaseKeyProvider implements IKeyProvider {
  protected readonly keyStore = new InMemoryKeyStore();
  protected readonly hashProvider = new Sha256HashProvider();

  protected abstract readonly supportedAlgorithm: CryptoAlgorithm;
  protected abstract readonly supportedCurve: EllipticCurve;

  protected async onConnect(): Promise<void> {
    this.hashProvider.configure();
  }

  protected async onDisconnect(): Promise<void> {
    this.keyStore.clear();
  }

  async generateKeyPair(request: KeyPairGenerationRequest): Promise<KeyPairProviderResult | null> {
    if (!this.isReady()) {
      return null;
    }

    if (request.algorithm !== this.supportedAlgorithm || request.curve !== this.supportedCurve) {
      return null;
    }

    const curveImpl = this.getCurveImplementation();
    const generated = curveImpl.generateKeyPair();
    const keyPairId = createKeyPairId();
    const publicKey = bytesToHex(generated.publicKey);

    this.keyStore.save({
      keyPairId,
      privateKey: generated.privateKey,
      publicKeyHex: publicKey,
      algorithm: this.supportedAlgorithm,
      curve: this.supportedCurve,
      status: 'generated',
      createdAt: Date.now(),
      rotatedAt: null,
    });

    return {
      keyPairId,
      publicKey,
      privateKeyReference: createPrivateKeyReference(keyPairId),
    };
  }

  async deriveAddress(
    keyPairId: KeyPairId,
    _addressType: string,
    _derivationPath?: string
  ): Promise<AddressProviderResult | null> {
    if (!this.isReady()) {
      return null;
    }

    const material = this.keyStore.get(keyPairId);
    if (!material || material.status === 'revoked' || material.status === 'compromised') {
      return null;
    }

    const publicKeyHash = this.hashProvider.hashHex(material.publicKeyHex);
    const checksum = this.hashProvider.hashHex(publicKeyHash).slice(0, 8);
    const address = `rtwo1${publicKeyHash.slice(0, 40)}`;

    return {
      address,
      publicKeyHash,
      checksum,
    };
  }

  async signMessage(
    keyPairId: KeyPairId,
    messageHash: MessageHashHex,
    algorithm: CryptoAlgorithm
  ): Promise<SignatureProviderResult | null> {
    if (!this.isReady() || algorithm !== this.supportedAlgorithm) {
      return null;
    }

    const material = this.keyStore.get(keyPairId);
    if (!material || material.status === 'revoked' || material.status === 'compromised') {
      return null;
    }

    if (!isValidHex(messageHash)) {
      return null;
    }

    const curveImpl = this.getCurveImplementation();
    const signature = curveImpl.sign(hexToBytes(messageHash), material.privateKey);

    return {
      signatureId: createSignatureId(),
      signature: bytesToHex(signature),
    };
  }

  async verifySignature(
    messageHash: MessageHashHex,
    signature: string,
    publicKey: PublicKeyHex,
    algorithm: CryptoAlgorithm
  ): Promise<boolean> {
    if (!this.isReady() || algorithm !== this.supportedAlgorithm) {
      return false;
    }

    if (!isValidHex(messageHash) || !isValidHex(signature) || !isValidHex(publicKey)) {
      return false;
    }

    const curveImpl = this.getCurveImplementation();
    const publicKeyBytes = hexToBytes(publicKey);

    if (!curveImpl.validatePublicKey(publicKeyBytes)) {
      return false;
    }

    try {
      return curveImpl.verify(hexToBytes(signature), hexToBytes(messageHash), publicKeyBytes);
    } catch {
      return false;
    }
  }

  async validatePublicKey(
    publicKey: PublicKeyHex,
    algorithm: CryptoAlgorithm,
    curve: EllipticCurve
  ): Promise<boolean> {
    if (!this.isReady() || algorithm !== this.supportedAlgorithm || curve !== this.supportedCurve) {
      return false;
    }

    if (!isValidHex(publicKey)) {
      return false;
    }

    return this.getCurveImplementation().validatePublicKey(hexToBytes(publicKey));
  }

  async rotateKey(keyPairId: KeyPairId): Promise<KeyPairProviderResult | null> {
    if (!this.isReady()) {
      return null;
    }

    const existing = this.keyStore.get(keyPairId);
    if (!existing || existing.status === 'revoked' || existing.status === 'compromised') {
      return null;
    }

    this.keyStore.updateStatus(keyPairId, 'rotated', Date.now());

    return this.generateKeyPair({
      algorithm: this.supportedAlgorithm,
      curve: this.supportedCurve,
      purpose: 'system',
      requestedBy: 'key-rotation',
    });
  }

  async revokeKey(keyPairId: KeyPairId): Promise<boolean> {
    if (!this.isReady()) {
      return false;
    }

    const material = this.keyStore.get(keyPairId);
    if (!material) {
      return false;
    }

    this.keyStore.updateStatus(keyPairId, 'revoked');
    return true;
  }

  async getKeyPair(keyPairId: KeyPairId): Promise<RTWOKeyPair | null> {
    if (!this.isReady()) {
      return null;
    }

    const material = this.keyStore.get(keyPairId);
    if (!material) {
      return null;
    }

    return {
      keyPairId: material.keyPairId,
      publicKey: material.publicKeyHex,
      privateKeyReference: createPrivateKeyReference(material.keyPairId),
      algorithm: material.algorithm,
      curve: material.curve,
      status: material.status,
      createdAt: material.createdAt,
      rotatedAt: material.rotatedAt,
    };
  }

  getDescriptor() {
    return {
      ...super.getDescriptor(),
      description: `${this.providerName}. Keys generated only on explicit request — no pre-seeded material.`,
    };
  }

  private getCurveImplementation(): CurveImplementation {
    if (this.supportedAlgorithm === 'Ed25519') {
      return CURVE_IMPLEMENTATIONS.Ed25519;
    }
    if (this.supportedAlgorithm === 'secp256k1') {
      return CURVE_IMPLEMENTATIONS.secp256k1;
    }
    throw new Error(`Unsupported algorithm: ${this.supportedAlgorithm}`);
  }
}
